import { ComegoTime } from "@timeghost/web-api";
import { addWeeks, startOfDay, startOfWeek } from "date-fns/esm";
import { clamp } from "lodash-es";
import { hoursToFormat } from "./utils";

export interface WorkingHourViolation {
	args?: Record<string, any>;
	date: string;
	entryID?: string;
	errorActAsWarning?: boolean;
	message: string;
	rule?: any;
	type: "maxHours" | "break" | "rest" | "averageWeeklyHours";
	value: string | number;
}

export type AverageWeeklyHoursRules = {
	averageWorkingHours?: number;
	averageWorkingHoursRange?: number;
};

const typeToTranslateKey = {
	maxHours: "errors.times.comego.dailyMaxHours-alert",
	break: "errors.times.comego.breakRuleNotice-alert",
	rest: "errors.times.comego.breakBetweenWorkingDays-alert",
	averageWeeklyHours: "errors.times.comego.averageWeeklyHours-alert",
};

export type EvaluateWorkingHoursOptions = {
	prevDayItem: ComegoTime;
	nextDayItem: ComegoTime;
};

const WEEK_OPTS = { weekStartsOn: 1 as const };

function weekKey(date: Date): number {
	return startOfWeek(date, WEEK_OPTS).getTime();
}

function entryDurationMs(time: ComegoTime, refNow = new Date()): number {
	const end = time.end ? new Date(time.end) : refNow;
	const start = new Date(time.start);
	return clamp(end.getTime() - start.getTime(), 0, Number.MAX_SAFE_INTEGER);
}

/** Ceil decimal hours to the next whole minute (e.g. 10s over the limit counts as 1 minute over). */
function ceilHoursToMinute(hours: number): number {
	return Math.ceil(hours * 60) / 60;
}

/** Rolling average weekly work hours over `averageWorkingHoursRange` weeks. */
export function evaluateWeeklyAverageHours(
	comegoTimes: ComegoTime[],
	allWorkTimes: ComegoTime[],
	rules: AverageWeeklyHoursRules,
	now = new Date()
): WorkingHourViolation[] {
	const maxAvg = rules.averageWorkingHours ?? 48;
	const rangeWeeks = rules.averageWorkingHoursRange ?? 24;
	if (!maxAvg || !rangeWeeks) return [];

	const comegoWork = comegoTimes
		.filter((t) => t.type === "work")
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
	if (!comegoWork.length) return [];

	const comegoIds = new Set(comegoTimes.map((t) => t.id));
	const processedComegoIds = new Set<string>();
	const violations: WorkingHourViolation[] = [];

	for (const entry of comegoWork) {
		processedComegoIds.add(entry.id);
		const windowEnd = new Date(entry.end || now);
		const anchorWeek = startOfWeek(windowEnd, WEEK_OPTS);
		const weeks = Array.from({ length: rangeWeeks }, (_, i) => addWeeks(anchorWeek, -(rangeWeeks - 1 - i)));
		const windowStart = weeks[0];

		let totalMs = 0;
		for (const weekStart of weeks) {
			const wk = weekKey(weekStart);
			const weekMs = allWorkTimes.reduce((sum, t) => {
				const start = new Date(t.start);
				if (weekKey(start) !== wk) return sum;
				if (start < windowStart || start > windowEnd) return sum;
				if (comegoIds.has(t.id) && !processedComegoIds.has(t.id)) return sum;
				return sum + entryDurationMs(t, now);
			}, 0);
			totalMs += weekMs;
		}

		const avgHours = ceilHoursToMinute(totalMs / rangeWeeks / 3_600_000);
		if (avgHours > maxAvg) {
			violations.push({
				type: "averageWeeklyHours",
				message: typeToTranslateKey.averageWeeklyHours,
				value: hoursToFormat(maxAvg),
				date: startOfDay(new Date(entry.start)).toISOString(),
				entryID: entry.id,
				errorActAsWarning: true,
				args: {
					averageWorkingHours: hoursToFormat(maxAvg),
					averageWorkingHoursRange: rangeWeeks,
					actualAverage: hoursToFormat(avgHours),
				},
			});
		}
	}

	return violations;
}
export function evaluateWorkingHours(
	times: ComegoTime[],
	workingHourRules: {
		dailyMaxHours?: number;
		breakBetweenWorkingDays?: number;
		minimumBreakDurationForCredit?: number;
		breaks?: Array<{ workingHours: number; minutesDue: number }>;
	},
	date = new Date(),
	options: EvaluateWorkingHoursOptions = {} as EvaluateWorkingHoursOptions
): WorkingHourViolation[] {
	const violations: WorkingHourViolation[] = [];
	const { nextDayItem, prevDayItem } = options;

	// Create working copy of times with temporary violation tracking
	const workingTimes = times
		.map((time) => ({
			...time,
			_tempViolations: new Set<string>(),
		}))
		.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

	let runningWorkMs = 0;
	let runningPauseMs = 0;

	const minBreakDurationForCreditMS = (workingHourRules?.minimumBreakDurationForCredit ?? 0.25) * 3.6e6;
	let continousWork = 0;
	let validBreakSum = 0;
	// Check rest period with previous day
	if (workingHourRules.breakBetweenWorkingDays && prevDayItem && workingTimes.length > 0) {
		const firstWork = workingTimes.find((x) => x.type === "work");
		if (firstWork) {
			const currentStart = new Date(firstWork.start);
			const prevEnd = new Date(prevDayItem.end);
			const restMs = currentStart.getTime() - prevEnd.getTime();
			const requiredRestMs = workingHourRules.breakBetweenWorkingDays * 3600 * 1000;

			if (restMs < requiredRestMs) {
				violations.push({
					type: "rest",
					message: typeToTranslateKey.rest,
					value: hoursToFormat(workingHourRules.breakBetweenWorkingDays),
					date: startOfDay(prevEnd).toISOString(),
					entryID: firstWork.id,
					args: {
						breakBetweenWorkingDays: hoursToFormat(workingHourRules.breakBetweenWorkingDays),
					},
				});
				firstWork._tempViolations.add("rest_prev");
			}
		}
	}
	for (let index = 0; index < workingTimes.length; index++) {
		const time = workingTimes[index];
		const end = time.end ? new Date(time.end) : new Date();
		const start = new Date(time.start);
		const diff = clamp(end.getTime() - start.getTime(), 0, Number.MAX_SAFE_INTEGER);

		if (time.type === "work") {
			runningWorkMs += diff;
			continousWork += diff;

			// Check daily maximum hours
			if (workingHourRules.dailyMaxHours) {
				const maxHoursMs = workingHourRules.dailyMaxHours * 3600 * 1000;
				if (runningWorkMs > maxHoursMs && !time._tempViolations.has("maxHours")) {
					violations.push({
						type: "maxHours",
						message: typeToTranslateKey.maxHours,
						value: hoursToFormat(workingHourRules.dailyMaxHours),
						date: startOfDay(start).toISOString(),
						entryID: time.id,
						args: {
							dailyMaxHours: hoursToFormat(workingHourRules.dailyMaxHours),
						},
					});
					time._tempViolations.add("maxHours");
				}
			}

			// Check break rules
			const breakRule = workingHourRules.breaks
				.slice()
				.sort((b, a) => a.workingHours - b.workingHours)
				.find((x) => {
					const workingHourMs = x.workingHours * 3600 * 1000;
					return workingHourMs < continousWork;
				});
			if (breakRule) {
				const breakKey = `break_${breakRule.workingHours}_${breakRule.minutesDue}`;
				violations.push({
					type: "break",
					message: typeToTranslateKey.break,
					value: continousWork,
					rule: breakRule,
					date: startOfDay(start).toISOString(),
					entryID: time.id,
					args: {
						workingHours: hoursToFormat(breakRule.workingHours),
						minutesDue: hoursToFormat(breakRule.minutesDue),
					},
				});
				time._tempViolations.add(breakKey);
			}
		} else if (time.type === "pause") {
			runningPauseMs += diff;
			if (diff >= minBreakDurationForCreditMS) {
				continousWork = 0;
				validBreakSum += diff;
			}
		}
	}
	const breakRule = workingHourRules.breaks
		.slice()
		.sort((b, a) => a.workingHours - b.workingHours)
		.find((x) => {
			const workingHourMs = x.workingHours * 3600 * 1000;
			const pauseHourMs = x.minutesDue * 3600 * 1000;
			return workingHourMs < runningWorkMs && pauseHourMs > validBreakSum;
		});
	if (breakRule) {
		const breakKey = `break_${breakRule.workingHours}_${breakRule.minutesDue}`;
		violations.push({
			type: "break",
			message: typeToTranslateKey.break,
			value: runningWorkMs,
			rule: breakRule,
			entryID: workingTimes[workingTimes.length - 1].id,
			date: startOfDay(new Date(workingTimes[0].start)).toISOString(),
			args: {
				workingHours: hoursToFormat(breakRule.workingHours),
				minutesDue: hoursToFormat(breakRule.minutesDue),
			},
		});
	}

	// Check rest period with next day
	if (workingHourRules.breakBetweenWorkingDays && nextDayItem && workingTimes.length > 0) {
		const lastWork = workingTimes.toReversed().find((x) => x.type === "work");
		if (lastWork) {
			const currentEnd = new Date(lastWork.end || new Date());
			const nextStart = new Date(nextDayItem.start);
			const restMs = nextStart.getTime() - currentEnd.getTime();
			const requiredRestMs = workingHourRules.breakBetweenWorkingDays * 3600 * 1000;

			if (restMs < requiredRestMs && !lastWork._tempViolations.has("rest_next")) {
				violations.push({
					type: "rest",
					message: typeToTranslateKey.rest,
					value: hoursToFormat(workingHourRules.breakBetweenWorkingDays),
					date: startOfDay(currentEnd).toISOString(),
					entryID: lastWork.id,
					args: {
						breakBetweenWorkingDays: hoursToFormat(workingHourRules.breakBetweenWorkingDays),
					},
				});
				lastWork._tempViolations.add("rest_next");
			}
		}
	}
	return violations;
	// return violations.map((d: any) => {
	//   if (!d.meta?.errors) d.meta = Object.assign({}, d.meta || {}, { errors: [] });
	//   return d;
	// });
}
